import {
  AccountType,
  formatStripePriceWithCurrency, GymTypes, IPaymentService, IPricingTableItem,
  StripeMetadataKeysForPricingTable,
  TimeSlotType,
} from 'fitbeat.models';
import { DbGymRepository } from '../../persistence';
import { GymTimeSlotsManager } from '../bookings';
import {
  buildOperatingHourEntry, buildWeekdaySummaryListItem,
  getGymOperatingHours, getWeekdayFormattedRangeString, IGymTimeslotBuilderOptions, IOperatingHours,
} from './utils/gym-timeslot-info-builder';

export class OrgConfigManager {
  constructor(private paymentService: IPaymentService, private gymTimeSlotsManager: GymTimeSlotsManager) {
  }

  public async getPricingTable(gymName: string): Promise<IPricingTableItem[]> {
    const gym = DbGymRepository.getByName(gymName);
    const prices = this.paymentService.getCachedPricesForAccount(gym.accountId);

    const showAtPositionKey: StripeMetadataKeysForPricingTable = 'showOnPricingTableAtPosition';
    const priceFeaturesKey: StripeMetadataKeysForPricingTable = 'showOnPricingTableFeaturesCsv';

    const pricesForPricingTable = prices
      .filter((price) => price.metadata[showAtPositionKey] != null)
      .sort((a, b) => parseInt(a.metadata[showAtPositionKey], 10) - parseInt(b.metadata[showAtPositionKey], 10));

    const pricingTable: IPricingTableItem[] = pricesForPricingTable.map((price) => ({
      name: price.description || 'Subscription Plan',
      features: price.metadata[priceFeaturesKey]?.split(','),
      priceId: price.priceId,
      formattedPriceWithCurrency: formatStripePriceWithCurrency(price.currency, price.unitAmount),
      recurringInterval: price?.recurring?.interval as 'month' | 'week' | 'day' | undefined,
      accountType: gym.type === GymTypes.Indoor ? AccountType.Studio : AccountType.Home,
    }));
    return pricingTable;
  }

  public async getExamplePricingTable(): Promise<IPricingTableItem[]> {
    const examplePricingTable = [
      {
        name: 'Example plan',
        features: ['feature1', 'feature2'],
        priceId: '1',
        formattedPriceWithCurrency: formatStripePriceWithCurrency('USD', 12345),
        recurringInterval: 'month' as 'month' | 'week' | 'day' | undefined,
        accountType: AccountType.Home,
      },
    ];
    return examplePricingTable;
  }

  public async getGymTimeSlotsInfo(
    gymName: string,
    options: IGymTimeslotBuilderOptions,
  ): Promise<IOperatingHours> {
    const gym = DbGymRepository.getByName(gymName);
    return getGymOperatingHours(
      gym,
      this.gymTimeSlotsManager,
      options,
    );
  }

  public getExampleGymTimeSlotsInfo(): IOperatingHours {
    const sampleTimes = [
      { start: '0530', end: '0630', type: TimeSlotType.OffPeak },
      { start: '0630', end: '0800', type: TimeSlotType.Peak },
      { start: '0800', end: '1200', type: TimeSlotType.OffPeak },
      { start: '1200', end: '1330', type: TimeSlotType.Peak },
      { start: '1330', end: '1600', type: TimeSlotType.OffPeak },
      { start: '1600', end: '1800', type: TimeSlotType.Peak },
      { start: '1800', end: '2030', type: TimeSlotType.OffPeak },
    ];
    const sampleOptions = {
      publicHolidays: {
        daysAhead: 0,
        from: new Date(),
      },
      titles: {},
    };

    const hourEntry = buildOperatingHourEntry('Sample Operating Hours', sampleTimes, sampleOptions, 'Sample');
    hourEntry.closedAllDayMessageHtml = 'This would only display when there are no times for this day';

    return {
      weekdaySummaries: [
        buildWeekdaySummaryListItem({
          fromDay: 'Sample day',
          toDay: 'Sample day',
          times: getWeekdayFormattedRangeString(sampleTimes) || 'sample times',
        }),
      ],
      days: [
        hourEntry,
      ],
    };
  }
}
